import { useEffect, useRef, useState } from 'react'
import { Collapse, Form, FormInstance, Input, Radio, Select, Switch } from 'antd'
import styled from 'styled-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faObjectsAlignCenterHorizontal,
	faObjectsAlignLeft,
	faObjectsAlignRight,
} from '@fortawesome/pro-duotone-svg-icons'

import { txt } from '../../../../../locales/i18n'
import { AnimationCollapse } from './animation'

export type ButtonContainerPanelProps = {
	propertyKey: string
	formRef: React.RefObject<FormInstance>
	onSectionChange: (changedValues: any, values: any) => void
}

export const ButtonContainerPanel = (props: ButtonContainerPanelProps) => {
	const enablePrimarySwitchRef = useRef<HTMLElement>(null)
	const enableSecondarySwitchRef = useRef<HTMLElement>(null)
	const [enablePrimary, setEnablePrimary] = useState(false)
	const [enableSecondary, setEnableSecondary] = useState(false)

	useEffect(() => {
		const enableSwitch = enablePrimarySwitchRef.current
		if (enableSwitch === null) return

		const buttonEnabled = enableSwitch.getAttribute('aria-checked') === 'true'
		setEnablePrimary(buttonEnabled)
	}, [enablePrimarySwitchRef])

	useEffect(() => {
		const enableSwitch = enableSecondarySwitchRef.current
		if (enableSwitch === null) return

		const buttonEnabled = enableSwitch.getAttribute('aria-checked') === 'true'
		setEnableSecondary(buttonEnabled)
	}, [])

	return (
		<ButtonPanelStyled>
			<div className="form-item-container">
				<label>{txt('cms.alignment')}</label>
				<Form.Item name={[props.propertyKey, 'container', 'placement']}>
					<Radio.Group>
						<Radio.Button value="left">
							<FontAwesomeIcon icon={faObjectsAlignLeft} />
						</Radio.Button>

						<Radio.Button value="center">
							<FontAwesomeIcon icon={faObjectsAlignCenterHorizontal} />
						</Radio.Button>

						<Radio.Button value="right">
							<FontAwesomeIcon icon={faObjectsAlignRight} />
						</Radio.Button>
					</Radio.Group>
				</Form.Item>
			</div>

			<CollapseStyled ghost>
				<Collapse.Panel header={txt('cms.primary')} key="primary" forceRender>
					<div className="row-container">
						<div style={{ flex: 1 }}>
							<label>{txt('cms.visible')}</label>
							<Form.Item name={[props.propertyKey, 'primary', 'visible']} valuePropName="checked">
								<Switch ref={enablePrimarySwitchRef} onChange={(checked) => setEnablePrimary(checked)} />
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.background')}</label>
							<Form.Item name={[props.propertyKey, 'primary', 'backgroundColor']}>
								<Input type="color" disabled={enablePrimary === false} />
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.color')}</label>
							<Form.Item name={[props.propertyKey, 'primary', 'textColor']}>
								<Input type="color" disabled={enablePrimary === false} />
							</Form.Item>
						</div>
					</div>

					<div className="row-container">
						<div style={{ flex: 1 }}>
							<label>{txt('cms.text')}</label>
							<Form.Item name={[props.propertyKey, 'primary', 'text']} noStyle>
								<Input required disabled={enablePrimary === false} />
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.design')}</label>
							<Form.Item name={[props.propertyKey, 'primary', 'design']}>
								<Select disabled={enablePrimary === false}>
									<Select.Option value="default">{txt('cms.default')}</Select.Option>
									<Select.Option value="simple">{txt('cms.simple')}</Select.Option>
									<Select.Option value="minimal">{txt('cms.minimal')}</Select.Option>
								</Select>
							</Form.Item>
						</div>
					</div>

					<div className="row-container">
						<div style={{ flex: 1 }}>
							<label>{txt('cms.action')}</label>
							<Form.Item name={[props.propertyKey, 'primary', 'action']}>
								<Select disabled={enablePrimary === false}>
									<Select.Option value="navigate">{txt('cms.navigate')}</Select.Option>
								</Select>
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.link')}</label>
							<Form.Item name={[props.propertyKey, 'primary', 'link']}>
								<Input disabled={enablePrimary === false} />
							</Form.Item>
						</div>
					</div>
				</Collapse.Panel>

				<Collapse.Panel header={txt('cms.secondary')} key="secondary" forceRender>
					<div className="row-container">
						<div style={{ flex: 1 }}>
							<label>{txt('cms.visible')}</label>
							<Form.Item name={[props.propertyKey, 'secondary', 'visible']} valuePropName="checked">
								<Switch ref={enableSecondarySwitchRef} onChange={(checked) => setEnableSecondary(checked)} />
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.background')}</label>
							<Form.Item name={[props.propertyKey, 'secondary', 'backgroundColor']}>
								<Input type="color" disabled={enableSecondary === false} />
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.color')}</label>
							<Form.Item name={[props.propertyKey, 'secondary', 'textColor']}>
								<Input type="color" disabled={enableSecondary === false} />
							</Form.Item>
						</div>
					</div>

					<div className="row-container">
						<div style={{ flex: 1 }}>
							<label>{txt('cms.text')}</label>
							<Form.Item name={[props.propertyKey, 'secondary', 'text']} noStyle>
								<Input required disabled={enableSecondary === false} />
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.design')}</label>
							<Form.Item name={[props.propertyKey, 'secondary', 'design']}>
								<Select disabled={enableSecondary === false}>
									<Select.Option value="default">{txt('cms.default')}</Select.Option>
									<Select.Option value="simple">{txt('cms.simple')}</Select.Option>
									<Select.Option value="minimal">{txt('cms.minimal')}</Select.Option>
								</Select>
							</Form.Item>
						</div>
					</div>

					<div className="row-container">
						<div style={{ flex: 1 }}>
							<label>{txt('cms.action')}</label>
							<Form.Item name={[props.propertyKey, 'secondary', 'action']}>
								<Select disabled={enableSecondary === false}>
									<Select.Option value="navigate">{txt('cms.navigate')}</Select.Option>
								</Select>
							</Form.Item>
						</div>

						<div style={{ flex: 1 }}>
							<label>{txt('cms.link')}</label>
							<Form.Item name={[props.propertyKey, 'secondary', 'link']}>
								<Input disabled={enableSecondary === false} />
							</Form.Item>
						</div>
					</div>
				</Collapse.Panel>
			</CollapseStyled>

			<AnimationCollapse
				propertyKey={props.propertyKey}
				formRef={props.formRef}
				onSectionChange={props.onSectionChange}
			/>
		</ButtonPanelStyled>
	)
}

const ButtonPanelStyled = styled.div`
	.placement-button-container {
		display: flex;
		width: 100%;
	}

	.placement-button {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.placement-icon-container {
		gap: 1ch;
		display: flex;
	}

	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}
`

const CollapseStyled = styled(Collapse)`
	padding: 0 !important;

	.ant-collapse-content-box {
		padding: 0 !important;
	}

	.row-container {
		display: flex;
		gap: 2ch;
	}

	.form-item-container {
		flex: 1;
	}
`
